import React, { useState } from 'react';
import { apiCall } from '../utils/api';
import { toast } from '../components/ui/sonner';
import {
  AlertTriangle, Phone, X, Shield, Stethoscope, ScanEye, UserX, HardHat, ShieldAlert, ChevronRight, FileWarning, Loader2,
} from 'lucide-react';

const REPORT_TYPES = [
  { id: 'criminal_activity', label: 'Criminal Activity',  desc: 'Theft, assault, illegal acts',           Icon: ShieldAlert,  tint: 'from-red-700 to-rose-700' },
  { id: 'medical',           label: 'Medical Emergency',  desc: 'Injury, illness, urgent medical need',   Icon: Stethoscope,  tint: 'from-red-500 to-pink-500' },
  { id: 'safety',            label: 'Safety Threat',      desc: 'Hazard, violence, weapon, intrusion',    Icon: ScanEye,      tint: 'from-orange-500 to-amber-500' },
  { id: 'harassment',        label: 'Harassment',         desc: 'Verbal abuse, discrimination, bullying', Icon: UserX,        tint: 'from-purple-600 to-fuchsia-600' },
  { id: 'accident',          label: 'Workplace Accident', desc: 'Slip, fall, equipment failure',          Icon: HardHat,      tint: 'from-amber-500 to-yellow-500' },
  { id: 'general',           label: 'Other',              desc: 'Anything else needing attention',        Icon: FileWarning,  tint: 'from-slate-600 to-slate-700' },
];

export default function ReportButton() {
  const [showModal, setShowModal] = useState(false);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [reportType, setReportType] = useState('');
  const [details, setDetails] = useState('');
  const [response, setResponse] = useState(null);

  const submit = async () => {
    if (!reportType) {
      toast.error('Please select a report type');
      return;
    }
    setSending(true);
    try {
      let location = {};
      if (navigator.geolocation) {
        try {
          const pos = await new Promise((resolve, reject) => navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 5000 }));
          location = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        } catch { /* permission denied or unavailable — ok */ }
      }
      const res = await apiCall('/api/worker/report', {
        method: 'POST',
        body: JSON.stringify({ type: reportType, message: details.trim() || undefined, location }),
      });
      setResponse(res);
      setSent(true);
      toast.success('Report submitted', { description: 'Help is on the way.' });
    } catch (e) {
      toast.error('Failed to submit report. If urgent, dial 911.', { description: e.message });
    } finally {
      setSending(false);
    }
  };

  const reset = () => { setSent(false); setReportType(''); setDetails(''); setResponse(null); };

  const selectedMeta = REPORT_TYPES.find((t) => t.id === reportType);

  return (
    <>
      {/* Floating Report Button */}
      <button
        onClick={() => { setShowModal(true); reset(); }}
        className="fixed bottom-24 right-6 z-40 group flex items-center gap-2 pl-3 pr-4 py-3 bg-gradient-to-br from-rose-600 to-red-700 hover:from-rose-700 hover:to-red-800 text-white rounded-full shadow-2xl shadow-rose-600/40 transition-all hover:scale-105 active:scale-95 ring-4 ring-rose-500/20"
        title="Submit a report"
        data-testid="report-btn"
      >
        <FileWarning className="w-5 h-5" />
        <span className="text-sm font-bold tracking-wide">Report</span>
      </button>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-slate-900/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowModal(false)}>
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-hidden shadow-2xl flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="report-modal">
            {/* Header */}
            <div className="bg-gradient-to-r from-rose-600 via-red-600 to-orange-600 px-5 py-4 flex items-center justify-between text-white">
              <div className="flex items-center gap-2.5">
                <div className="p-1.5 rounded-lg bg-white/15 ring-1 ring-white/20">
                  <FileWarning className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-lg font-bold leading-tight">Submit a Report</h3>
                  <p className="text-[11px] text-white/80">High-priority alert sent to admin, supervisor, HR & on-call</p>
                </div>
              </div>
              <button onClick={() => setShowModal(false)} className="p-1.5 hover:bg-white/15 rounded-lg transition" data-testid="report-close-btn">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-5 overflow-y-auto">
              {!sent ? (
                <>
                  <p className="text-xs text-slate-500 mb-3">Select the type of report. Your location and shift context are shared with the response team.</p>
                  <div className="space-y-1.5 mb-4">
                    {REPORT_TYPES.map(({ id, label, desc, Icon, tint }) => (
                      <button
                        key={id}
                        onClick={() => setReportType(id)}
                        className={`w-full text-left flex items-center gap-3 px-3 py-2.5 rounded-xl border-2 transition-all ${
                          reportType === id ? 'border-rose-500 bg-rose-50 shadow-sm' : 'border-slate-200 hover:border-slate-300 bg-white'
                        }`}
                        data-testid={`report-type-${id}`}
                      >
                        <div className={`p-2 rounded-lg bg-gradient-to-br ${tint} text-white flex-shrink-0`}>
                          <Icon className="w-4 h-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-sm text-slate-900">{label}</p>
                          <p className="text-[11px] text-slate-500">{desc}</p>
                        </div>
                        {reportType === id && <ChevronRight className="w-4 h-4 text-rose-500" />}
                      </button>
                    ))}
                  </div>

                  <label className="block">
                    <span className="text-xs font-bold text-slate-700 uppercase tracking-wider">Details <span className="text-slate-400 font-normal normal-case">(optional but encouraged)</span></span>
                    <textarea
                      rows={3}
                      value={details}
                      onChange={(e) => setDetails(e.target.value)}
                      placeholder="What happened? Any names, descriptions, or context that can help…"
                      className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-rose-500 focus:border-rose-500"
                      data-testid="report-details"
                    />
                  </label>

                  <button
                    onClick={submit}
                    disabled={sending || !reportType}
                    className={`mt-4 w-full py-3 rounded-xl text-white font-bold flex items-center justify-center gap-2 transition disabled:opacity-50 ${
                      selectedMeta ? `bg-gradient-to-r ${selectedMeta.tint}` : 'bg-rose-600'
                    }`}
                    data-testid="submit-report-btn"
                  >
                    {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <FileWarning className="w-5 h-5" />}
                    {sending ? 'Submitting…' : 'Submit Report'}
                  </button>

                  <a href="tel:911" className="mt-3 flex items-center gap-2.5 p-2.5 bg-slate-50 rounded-xl hover:bg-slate-100 transition" data-testid="call-911-btn">
                    <div className="p-1.5 bg-rose-100 rounded-lg">
                      <Phone className="w-4 h-4 text-rose-600" />
                    </div>
                    <div>
                      <p className="font-bold text-sm text-slate-900">Call 911</p>
                      <p className="text-[11px] text-slate-500">For life-threatening emergencies</p>
                    </div>
                  </a>
                </>
              ) : (
                <div className="text-center py-4">
                  <div className="w-14 h-14 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Shield className="w-7 h-7 text-emerald-600" />
                  </div>
                  <h4 className="text-lg font-bold text-slate-900 mb-1">Report Received</h4>
                  <p className="text-sm text-slate-500 mb-4">Notifications dispatched to admin, supervisor, HR, and on-call contacts.</p>

                  {response?.notified_recipients && (
                    <div className="grid grid-cols-3 gap-2 mb-4">
                      {Object.entries(response.notified_recipients).map(([k, v]) => (
                        <div key={k} className="bg-slate-50 rounded-lg p-2 text-center">
                          <p className="text-[10px] uppercase font-bold text-slate-500">{k.replace('_', ' ')}</p>
                          <p className="text-lg font-bold text-slate-900">{v}</p>
                        </div>
                      ))}
                    </div>
                  )}

                  <a href="tel:911" className="flex items-center justify-center gap-2 py-2.5 bg-rose-600 text-white rounded-xl font-medium hover:bg-rose-700 transition mb-2">
                    <Phone className="w-4 h-4" /> Call 911
                  </a>
                  <button onClick={() => setShowModal(false)} className="text-sm text-slate-500 hover:text-slate-700 transition">Close</button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
